/* istanbul ignore file - 3D aspects better tested visually */

import { Toast } from '@components/Toast';
import {
  faAtomSimple,
  faCloudExclamation,
  faRedo,
  faSave,
  faTrash,
  faUndo,
  faUpDownLeftRight,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useConfiguration } from '@partful/features/configurations/lib/useConfiguration';
import { useAnimation } from '@partful/features/instructions/animations/lib/useAnimation';
import { useAnimationBuilder } from '@partful/features/instructions/animations/lib/useAnimationBuilder';
import { useSaveAnimation } from '@partful/features/instructions/animations/lib/useSaveAnimation';
import { Animation } from '@partful/features/instructions/animations/model/animation';
import { AnimationState } from '@partful/features/instructions/animations/model/animationState';
import { useSegment } from '@partful/features/instructions/segments/lib/useSegment';
import { useCreateStep } from '@partful/features/instructions/steps/lib/useCreateStep';
import { useDeleteStep } from '@partful/features/instructions/steps/lib/useDeleteStep';
import { useListSteps } from '@partful/features/instructions/steps/lib/useListSteps';
import { ErrorPage } from '@partful/features/instructions/ui/ErrorPage';
import { LoadingPage } from '@partful/features/instructions/ui/LoadingPage';
import { ModelAnimator } from '@partful/features/instructions/ui/ModelAnimator/ModelAnimator';
import { ObjectPropertiesPanel } from '@partful/features/instructions/ui/ModelEditor/ObjectPropertiesPanel/ObjectPropertiesPanel';
import { TransformMultipleControlsProps } from '@partful/features/instructions/ui/ModelEditor/TransformMultipleControls/TransformMultipleControls';
import { useQueryParams } from '@partful/shared/lib/hooks/useQueryParams';
import { useUndoRedo } from '@partful/shared/lib/hooks/useUndoRedo';
import { Button } from '@partful/shared/ui/Button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogPanel } from '@partful/shared/ui/Dialog/Dialog';
import { Surface } from '@partful/shared/ui/Surface';
import { Typography } from '@partful/shared/ui/Typography';
import { useTenant } from '@providers/Tenant';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { Object3D } from 'three';
import { ModelEditor } from '../../../features/instructions/ui/ModelEditor/ModelEditor';
import { useModel } from '../viewer/lib/useModel';
import styles from './StepBuilderPage.module.scss';
import { AnimationToolbar } from './ui/AnimationToolbar/AnimationToolbar';
import { KeyframeEditor } from './ui/KeyframeEditor/KeyframeEditor';
import { StepContent } from './ui/StepContent/StepContent';
import { StepNavigation } from './ui/StepNavigation/StepNavigation';

export function StepBuilderPage() {
  const { instructionId, versionId, segmentId } = useParams<{
    instructionId: string;
    versionId: string;
    segmentId: string;
  }>();
  const { t: i18n } = useTranslation();
  const { location, replace } = useHistory();
  const { accountId: account } = useTenant();
  const { configurationId } = useParams<{ configurationId: string }>();
  const { data: configuration } = useConfiguration(configurationId);

  const { lang = 'en-GB', productName } = useQueryParams();
  const [languageCode, setLanguageCode] = useState(lang);

  const { data: segment, isLoading: isLoadingSegment } = useSegment(instructionId, versionId, segmentId, languageCode);
  const { data: steps, isLoading: isLoadingSteps } = useListSteps(instructionId, versionId, segmentId, languageCode);
  const [confirmDelete, setConfirmDelete] = useState(false);

  /* Step states */
  const [currentStepId, setCurrentStepId] = useState<string | undefined>(location.hash.substring(1).split('-')[0]);
  const currentStep = useMemo(() => steps?.find((s) => s.id === currentStepId) ?? steps?.at(0), [currentStepId, steps]);

  /* Step animation state */
  const { data: originalAnimation, isLoading: isLoadingAnimation } = useAnimation(currentStep?.id);
  const [animationLoaded, setAnimationLoaded] = useState(false);

  // - Use undo/redo history stack for animations
  const {
    value: currentAnimation,
    canUndo,
    canRedo,
    reset: resetAnimation,
    push: pushAnimation,
    undo,
    redo,
  } = useUndoRedo<Animation>({ channels: {} }, 50);

  // Reset animation history on step change
  useEffect(() => {
    if (originalAnimation && !animationLoaded) {
      setAnimationLoaded(true);
      resetAnimation(originalAnimation);
    }
  }, [animationLoaded, originalAnimation, resetAnimation]);

  // Push changes to the animation into the undo history
  const handleAnimationChange = useCallback(
    (changedAnimation: Animation) => {
      pushAnimation(changedAnimation);
    },
    [pushAnimation]
  );
  const { animation, addKeyframe, removeKeyframe, toggleObjectVisible, toggleObjectHighlight } = useAnimationBuilder(
    currentAnimation,
    handleAnimationChange
  );

  // Handle animation saving
  const {
    mutateAsync: saveAnimation,
    isSuccess: isSuccessSaveAnimation,
    isError: isErrorSaveAnimation,
    isLoading: isSavingAnimation,
  } = useSaveAnimation();

  const handleSaveAnimation = useCallback(async () => {
    try {
      if (currentStep) {
        await saveAnimation({
          stepId: currentStep.id,
          animation,
        });
      }
    } catch (e) {
      // Don't need to handle this here
    }
  }, [currentStep, saveAnimation, animation]);

  // Handle step changes
  const handleStepChange = useCallback(
    (stepId?: string) => {
      if (stepId !== currentStep?.id) {
        setCurrentStepId(stepId);
        setAnimationLoaded(false);
        resetAnimation({ channels: {} });
        setSelected([]);
        setTime(0);
      }
    },
    [currentStep?.id, resetAnimation]
  );

  useEffect(() => {
    if (segment && steps && currentStep) {
      const segmentName = segment.content?.name;
      const stepName = currentStep.content?.name ?? `Step${steps.indexOf(currentStep) + 1}`;

      const searchParams = new URLSearchParams(location?.search);
      if (segmentName) {
        searchParams.set('segmentName', segmentName);
      }
      searchParams.set('stepName', stepName);

      const search = `?${searchParams.toString()}`;
      const hash = `#${currentStep.id}`;

      if (location.search !== search || location.hash !== hash) {
        replace({ search, hash });
      }
    }
  }, [segment, steps, currentStep, location.search, location.hash, replace]);

  const {
    mutateAsync: deleteStep,
    isSuccess: isSuccessDeletingStep,
    isError: isErrorDeletingStep,
    isLoading: isDeletingStep,
  } = useDeleteStep();
  const handleDeleteStep = useCallback(async () => {
    try {
      if (currentStep && steps) {
        await deleteStep({
          versionId,
          instructionId,
          segmentId,
          stepId: currentStep.id,
        });
        const currentIndex = steps.findIndex((step) => step.id === currentStep.id);
        const newStep = steps[currentIndex - 1];
        handleStepChange(newStep.id);
      }
    } catch (e) {
      // Don't need to handle this here
    }
  }, [currentStep, steps, deleteStep, versionId, instructionId, segmentId, handleStepChange]);

  const {
    mutateAsync: createStep,
    isLoading: isCreatingStep,
    isSuccess: isSuccessCreatingStep,
    isError: isErrorCreatingStep,
  } = useCreateStep();
  const handleCreateStep = useCallback(async () => {
    try {
      if (steps) {
        const step = await createStep({ instructionId, versionId, segmentId, args: { order: steps.length } });
        handleStepChange(step?.id);
      }
    } catch (e) {
      // Don't have to handle this here
    }
  }, [createStep, handleStepChange, instructionId, segmentId, steps, versionId]);

  /* Load 3D model */
  const [{ data: originalModel, fetching: isLoadingModel, error: isErrorLoadingModel }] = useModel({
    account,
    product: productName,
  });

  /* Pre-process model to capture original data */
  const model = useMemo(() => {
    const model = originalModel?.clone(true);

    if (model) {
      model.userData = { ...model.userData, step: currentStep?.id };
      model.traverse((node) => {
        node.userData = {
          ...node.userData,
          originalPosition: node.position.clone(),
          originalRotation: node.quaternion.clone(),
        };
      });
    }

    return model;
  }, [originalModel, currentStep?.id]);

  /* Animation playback state */
  const [time, setTime] = useState(0);
  const [loop, setLoop] = useState(false);
  const [animationState, setAnimationState] = useState<AnimationState>(AnimationState.Stopped);
  const toggleLoop = () => setLoop((it) => !it);
  const handleTimeChange = useCallback((time: number) => {
    setTime(time);
  }, []);
  const onFinished = useCallback(() => {
    setAnimationState(AnimationState.Stopped);
  }, []);

  /* Selection state */
  const [selected, setSelected] = useState<Object3D[]>([]);
  const handleSelect = useCallback((selection: Object3D[]) => {
    setSelected(selection);
  }, []);
  const selectionHasKeyframe = useMemo(
    () =>
      selected?.some((object) => animation?.channels[object.name]?.keyframes.find((kf) => kf.time === time) != null),
    [animation, selected, time]
  );

  /* Transform state */
  const [transformMode, setTransformMode] = useState<TransformMultipleControlsProps['mode']>('translate');
  const sceneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleKeyboardShortcuts(e: KeyboardEvent) {
      switch (e.key) {
        case 'M':
        case 'm': {
          setTransformMode('translate');
          break;
        }
        case 'R':
        case 'r': {
          setTransformMode(e.shiftKey ? 'rotate-individually' : 'rotate');
          break;
        }
        default:
          break;
      }
    }

    const sceneEl = sceneRef.current;
    sceneEl?.addEventListener('keypress', handleKeyboardShortcuts);
    return () => {
      sceneEl?.removeEventListener('keypress', handleKeyboardShortcuts);
    };
  });

  /* Handle animation actions */
  const handleAddKeyframe = useCallback(() => {
    addKeyframe(time, selected);
  }, [addKeyframe, selected, time]);
  const handleRemoveKeyframe = useCallback(() => {
    removeKeyframe(time, selected);
  }, [removeKeyframe, selected, time]);
  const handleVisibilityToggle = useCallback(
    (object: Object3D) => {
      toggleObjectVisible([object]);
    },
    [toggleObjectVisible]
  );
  const handleHighlightToggle = useCallback(
    (object: Object3D) => {
      toggleObjectHighlight([object]);
    },
    [toggleObjectHighlight]
  );

  if (isLoadingSegment || isLoadingSteps) {
    return <LoadingPage />;
  }

  return (
    <>
      <div className={styles.page}>
        <StepContent
          instructionId={instructionId}
          versionId={versionId}
          segment={segment!}
          step={currentStep!}
          languageCode={languageCode}
          onChangeLanguage={(languageCode) => setLanguageCode(languageCode)}
        />
        <div ref={sceneRef} className={styles.scene} tabIndex={-1}>
          <StepNavigation
            className={styles.stepNavigation}
            steps={steps}
            activeStep={currentStep}
            onChange={(step) => handleStepChange(step.id)}
            onCreate={handleCreateStep}
            canCreate={!isCreatingStep}
          />
          <div className={styles.editor}>
            {isLoadingModel && <LoadingPage />}
            {isErrorLoadingModel && (
              <ErrorPage
                icon={<FontAwesomeIcon icon={faCloudExclamation} />}
                title={i18n('instructions:viewer.failedToLoadModelError.title')}
                message={i18n('instructions:viewer.failedToLoadModelError.message')}
              />
            )}
            {model && !isLoadingModel && !isErrorLoadingModel && (
              <ModelEditor
                model={model}
                canSelect={animationState !== AnimationState.Playing}
                canTransform
                transformMode={transformMode}
                viewerProps={{
                  swapFacing: configuration?.handedness === 'LHS',
                  swapYZAxis: configuration?.axisOrientation === 'Z-Up',
                  frameloop: animationState === AnimationState.Playing ? 'always' : 'demand',
                  children: animation != null && (
                    <ModelAnimator
                      animation={animation}
                      state={animationState}
                      loop={loop}
                      time={time}
                      onTick={handleTimeChange}
                      onFinished={onFinished}
                    />
                  ),
                }}
                selected={animationState !== AnimationState.Playing ? selected : undefined}
                onSelect={handleSelect}
                interfaceElements={{
                  topLeft: (
                    <div className={styles.settings}>
                      <Button size="small" variant="secondary" onClick={undo} disabled={!canUndo}>
                        <FontAwesomeIcon icon={faUndo} />
                        Undo
                      </Button>
                      <Button size="small" variant="secondary" onClick={redo} disabled={!canRedo}>
                        <FontAwesomeIcon icon={faRedo} />
                        Redo
                      </Button>
                    </div>
                  ),
                  bottomLeft: animationState !== AnimationState.Playing &&
                    selected.length > 0 &&
                    selected.some((selected) => selected.visible !== false) && (
                      <div className={styles.transformControls}>
                        <Button
                          size="small"
                          variant={transformMode === 'translate' ? 'primary' : 'secondary'}
                          onClick={() => setTransformMode('translate')}
                        >
                          <FontAwesomeIcon icon={faUpDownLeftRight} />
                          {i18n('instructions:stepBuilder.editor.move')} (m)
                        </Button>
                        <Button
                          size="small"
                          variant={transformMode.startsWith('rotate') ? 'primary' : 'secondary'}
                          onClick={() =>
                            setTransformMode((current) => (current === 'rotate' ? 'rotate-individually' : 'rotate'))
                          }
                        >
                          <FontAwesomeIcon icon={faAtomSimple} />
                          {i18n('instructions:stepBuilder.editor.rotate')} (r)
                          {transformMode === 'rotate-individually' && '*'}
                        </Button>
                      </div>
                    ),
                  bottomRight: <ObjectPropertiesPanel />,
                }}
              />
            )}
          </div>
        </div>
        <AnimationToolbar
          disabled={isLoadingAnimation}
          className={styles.sceneControls}
          animationState={animationState}
          canSaveKeyframe={selected.length > 0}
          onSaveKeyframe={handleAddKeyframe}
          canRemoveKeyframe={selectionHasKeyframe}
          onRemoveKeyframe={handleRemoveKeyframe}
          isLooping={loop}
          onGoToStart={() => handleTimeChange(0)}
          onGoToEnd={() => handleTimeChange(10)}
          onLoop={toggleLoop}
          onPause={() => setAnimationState(AnimationState.Paused)}
          onPlay={() => setAnimationState(AnimationState.Playing)}
          onStop={() => setAnimationState(AnimationState.Stopped)}
        />

        <Surface className={styles.sceneComponents}>
          {steps && currentStep && (
            <>
              <div className={styles.timeline}>
                <KeyframeEditor
                  model={model}
                  animation={animation}
                  time={time}
                  onChangeTime={handleTimeChange}
                  selection={selected}
                  onSelect={handleSelect}
                  onToggleVisibility={handleVisibilityToggle}
                  onToggleHighlight={handleHighlightToggle}
                />
              </div>
              <div className={styles.actions}>
                <Button
                  name="instructions_step_editor_save_step_click"
                  data-testid="save-step-btn"
                  disabled={isLoadingAnimation || isSavingAnimation || isDeletingStep}
                  variant="primary"
                  onClick={() => handleSaveAnimation()}
                >
                  <FontAwesomeIcon icon={faSave} />
                  {i18n('instructions:stepBuilder.saveAnimation')}
                </Button>
                <Button
                  name="instructions_stepeditor_delete_step_click"
                  data-testid="delete-step-btn"
                  disabled={!currentStep || isDeletingStep}
                  variant="destructive"
                  onClick={() => setConfirmDelete(true)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  {i18n('instructions:stepBuilder.deleteStep')}
                </Button>
              </div>
              <Dialog
                open={confirmDelete}
                modal
                onClose={
                  /* istanbul ignore next - not testing native <dialog> behaviour */ () => setConfirmDelete(false)
                }
              >
                <DialogPanel>
                  <DialogHeader>
                    <Typography as="p" appearance="heading5" overflow="hidden" textOverflow="ellipsis">
                      {i18n('instructions:step.actions.deleteConfirm.title', {
                        stepName: currentStep.content?.name
                          ? `'${currentStep.content.name}'`
                          : steps.indexOf(currentStep) + 1,
                      })}
                    </Typography>
                  </DialogHeader>
                  <DialogContent>{i18n('instructions:step.actions.deleteConfirm.message')}</DialogContent>
                  <DialogFooter>
                    <Button
                      name="instructions_stepeditor_delete_step_confirm_click"
                      data-testid="confirm-delete-step-btn"
                      variant="destructive"
                      onClick={async () => {
                        await handleDeleteStep();
                        setConfirmDelete(false);
                      }}
                      disabled={isDeletingStep}
                    >
                      {i18n('instructions:common.delete')}
                    </Button>
                    <Button
                      name="instructions_stepeditor_delete_step_cancel_confirm_click"
                      variant="tertiary"
                      onClick={/* istanbul ignore next */ () => setConfirmDelete(false)}
                      disabled={isDeletingStep}
                    >
                      {i18n('instructions:common.cancel')}
                    </Button>
                  </DialogFooter>
                </DialogPanel>
              </Dialog>
            </>
          )}
        </Surface>
      </div>
      <Toast
        dependency={isErrorCreatingStep}
        severity="error"
        title={i18n('instructions:step.actions.createError.title')}
        message={i18n('instructions:step.actions.createError.message')}
        testId="create-step-error-message"
      />
      <Toast
        dependency={isSuccessCreatingStep}
        severity="success"
        title={i18n('instructions:step.actions.createSuccess.title')}
        testId="create-step-success-message"
      />
      <Toast
        dependency={isErrorDeletingStep}
        severity="error"
        title={i18n('instructions:step.actions.deleteError.title.title')}
        message={i18n('instructions:step.actions.deleteError.title.message')}
        testId="delete-step-error-message"
      />
      <Toast
        dependency={isSuccessDeletingStep}
        severity="success"
        title={i18n('instructions:step.actions.deleteSuccess.title')}
        testId="delete-step-success-message"
      />
      <Toast
        dependency={isErrorSaveAnimation}
        severity="error"
        title={i18n('instructions:step.actions.saveAnimationError.title')}
        testId="save-animation-error-message"
      />
      <Toast
        dependency={isSuccessSaveAnimation}
        severity="success"
        title={i18n('instructions:step.actions.saveAnimationSuccess.title')}
        testId="save-animation-success-message"
      />
    </>
  );
}
